import type { SearchAdapterOptions } from "src/types/adapters/search.type";
import type { PropsWithConstructorClient } from ".";
import type {
  ConstructorListingRequest,
  ListingFacets,
  ListingParams,
  ListingResult,
} from "src/types/adapters/listing.type";
import {
  buildSelectedFacetFilters,
  findLeafSiblingsGroupId,
  getFacetConfig,
  getTotalResults,
  mapSortOptions,
  mapToCatalogProduct,
  mergeSiblingCategories,
  toListingFacets,
} from "./utils/helpers";
import { CONSTRUCTOR_SECTIONS } from "src/constants/constructor";
import type { ConstructorProductData } from "./utils/types";
import {
  CONSTRUCTOR_FACETS,
  DEFAULT_FILTERS,
  DEFAULT_SORT_DIRECTION,
  DEFAULT_SORT_KEY,
  LISTING_FACET_TO_CONSTRUCTOR,
  LISTING_TO_CONSTRUCTOR_SORT_KEY,
  PRODUCT_LIST_DATA_FIELDS,
} from "./utils/constants";
import { DEFAULT_PRODUCTS_PER_PAGE } from "src/utils/constants";
import { isListingSortKey } from "src/utils/type-guards";
import { localeFix, ShopType } from "src/utils/genderLocale/genderLocale";
import type { ConstructorClient } from "src/lib/constructor";

type ListingProps = PropsWithConstructorClient<{
  options: SearchAdapterOptions;
  params: ListingParams;
}>;

/**
 * Keeps the whole last level of the category rail visible on a leaf category by
 * topping its facet up with the leaf's siblings
 */
async function withLeafSiblings({
  client,
  facets,
  categoryId,
  filters,
}: {
  client: ConstructorClient;
  facets: ListingFacets;
  categoryId?: string;
  filters: Record<string, unknown>;
}): Promise<ListingFacets> {
  const parentGroupId =
    client.env === "server"
      ? findLeafSiblingsGroupId(facets, categoryId)
      : undefined;

  if (!parentGroupId) {
    return facets;
  }

  const {
    [LISTING_FACET_TO_CONSTRUCTOR.category]: _applied,
    ...siblingFilters
  } = filters;

  try {
    const { response } = await client.browse(
      CONSTRUCTOR_FACETS.GROUP_ID,
      parentGroupId,
      {
        section: CONSTRUCTOR_SECTIONS.PRODUCTS,
        resultsPerPage: 1,
        page: 1,
        filters: siblingFilters,
      }
    );

    return mergeSiblingCategories(
      facets,
      toListingFacets(response.facets),
      parentGroupId
    );
  } catch (error) {
    console.error(
      "An error occurred while fetching sibling categories:",
      error
    );

    return facets;
  }
}

export async function listing({
  client,
  params,
}: ListingProps): Promise<ListingResult> {
  try {
    const resultsPerPage = params.productsPerPage || DEFAULT_PRODUCTS_PER_PAGE;
    const selectedFacetFilters = buildSelectedFacetFilters(
      params.appliedFacets
    );
    const filters = {
      ...(params.gender && params.gender !== ShopType.All
        ? { genders: localeFix({ gender: params.gender }) }
        : {}),
      ...selectedFacetFilters,
      ...DEFAULT_FILTERS,
    };

    if (params.productListType === "SEARCH_PLP") {
      const { response, result_id, request } = await client.search(
        params.query,
        {
          section: CONSTRUCTOR_SECTIONS.PRODUCTS,
          fmtOptions: {
            fields: [...PRODUCT_LIST_DATA_FIELDS],
          },
          page: (params.page ?? 0) + 1,
          filters,
          resultsPerPage,
          sortBy: isListingSortKey(params.sort)
            ? LISTING_TO_CONSTRUCTOR_SORT_KEY[params.sort]
            : params.sort || DEFAULT_SORT_KEY,
          sortOrder: params.sortDirection || DEFAULT_SORT_DIRECTION,
        }
      );

      if ("redirect" in response) {
        throw new Error("Unexpected redirect from Constructor search");
      }

      const totalResults = getTotalResults(response);

      return {
        products:
          response.results.map((item) =>
            mapToCatalogProduct(item.data as ConstructorProductData, item.value)
          ) || [],
        facets: toListingFacets(response.facets),
        sortOptions: mapSortOptions(response.sort_options),
        totalResults,
        totalPages: totalResults ? Math.ceil(totalResults / resultsPerPage) : 0,
        resultId: result_id,
        request: request as ConstructorListingRequest,
      };
    }

    const { facetName, facetValue, section } = getFacetConfig(params);

    const baseBrowseOptions = {
      resultsPerPage,
      fmtOptions: {
        fields: [...PRODUCT_LIST_DATA_FIELDS],
      },
      page: (params.page ?? 0) + 1,
      sortBy: isListingSortKey(params.sort)
        ? LISTING_TO_CONSTRUCTOR_SORT_KEY[params.sort]
        : params.sort || DEFAULT_SORT_KEY,
      sortOrder: params.sortDirection || DEFAULT_SORT_DIRECTION,
    };

    const browseOptions = { ...baseBrowseOptions, filters };

    // Constructor Collections dual-run fallback: a collection not yet recreated
    // natively either throws a 404 from collection_id/Collections (confirmed:
    // Constructor errors on an unknown collection_id, it does not return a
    // "successful but empty" response) or, once the id resolves, can still come
    // back with zero results. Both qualify for a retry against the legacy
    // data.collections facet on Products using the curations id. Remove once
    // every collection exists natively in Constructor (see migration plan).
    // Note: the tracking beacon (CnstrcListingMarker) always reports the native
    // collection_id/slug filter, even when this fallback serves the results.
    const isCollectionsPlp = params.productListType === "COLLECTIONS_PLP";
    const collectionId = isCollectionsPlp ? params.collectionId : undefined;

    // Native Constructor collections are manually curated and some aren't
    // tagged with gender/is_deleted/designer_only facet values, which can
    // zero out an otherwise valid collection when those filters apply
    // (confirmed: took a 14-product collection down to 0). Try the fully
    // filtered browse first so gender is enforced whenever the collection
    // supports it, and only drop to refinement-only filters (size, brand,
    // color, price, ...) if that comes back empty. The legacy `collections`
    // facet fallback below keeps filtering as before, unchanged.
    const refinementOnlyBrowseOptions =
      Object.keys(selectedFacetFilters).length > 0
        ? { ...baseBrowseOptions, filters: selectedFacetFilters }
        : baseBrowseOptions;

    const browseLegacyCollections = (legacyCollectionId: string) =>
      client.browse(CONSTRUCTOR_FACETS.COLLECTIONS, legacyCollectionId, {
        section: CONSTRUCTOR_SECTIONS.PRODUCTS,
        ...browseOptions,
      });

    let browseResult;
    try {
      browseResult = await client.browse(facetName, facetValue, {
        section,
        ...browseOptions,
      });

      if (isCollectionsPlp && getTotalResults(browseResult.response) === 0) {
        console.error(
          `[Constructor Adapter] Collection "${facetValue}" retried without gender/default filters: fully-filtered native browse returned zero results.`
        );
        browseResult = await client.browse(facetName, facetValue, {
          section,
          ...refinementOnlyBrowseOptions,
        });
      }

      if (collectionId && getTotalResults(browseResult.response) === 0) {
        console.error(
          `[Constructor Adapter] Collection "${facetValue}" fell back to legacy collections facet: native browse returned zero results.`
        );
        browseResult = await browseLegacyCollections(collectionId);
      }
    } catch (error) {
      if (!collectionId) {
        throw error;
      }
      console.error(
        `[Constructor Adapter] Collection "${facetValue}" fell back to legacy collections facet: native browse threw.`,
        error
      );
      browseResult = await browseLegacyCollections(collectionId);
    }

    const { response, result_id, request } = browseResult;
    const totalResults = getTotalResults(response);

    return {
      products:
        response.results.map((item) =>
          mapToCatalogProduct(item.data as ConstructorProductData, item.value)
        ) || [],
      facets: await withLeafSiblings({
        client,
        facets: toListingFacets(response.facets),
        categoryId:
          facetName === CONSTRUCTOR_FACETS.GROUP_ID ? facetValue : undefined,
        filters,
      }),
      sortOptions: mapSortOptions(response.sort_options),
      totalResults,
      totalPages: totalResults ? Math.ceil(totalResults / resultsPerPage) : 0,
      resultId: result_id,
      request: request as ConstructorListingRequest,
    };
  } catch (error) {
    console.error(
      "[Constructor Adapter] An error occurred while fetching listing results:",
      error
    );

    return {
      facets: new Map(),
      products: [],
      sortOptions: {},
      totalPages: 0,
      totalResults: 0,
    };
  }
}
